import React, { useCallback, useEffect } from 'react';
import { Typography } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useClientTenantStore } from '../../../store/clientTenantStore';
import { useBookingFlowStore } from '../../../store/bookingFlowStore';
import { useMainButton } from '../../../hooks/useMainButton';
import type { ClientProvider } from '../../../types/client';

interface Props {
  onNext: () => void;
}

export const ProviderStep: React.FC<Props> = ({ onNext }) => {
  const { t } = useTranslation('customer');
  const providers = useClientTenantStore((s) => s.providers);
  const providerId = useBookingFlowStore((s) => s.providerId);
  const providerDisplayName = useBookingFlowStore((s) => s.providerDisplayName);
  const setProvider = useBookingFlowStore((s) => s.setProvider);
  const setProviderDisplayName = useBookingFlowStore((s) => s.setProviderDisplayName);

  // Tanlangan provider mavjud ekan, displayName'ni doimo eng so'nggi providers
  // ro'yxatidan oladi. Bu til o'zgarganda ham (providers backenddan ru/uz/en
  // tarjimasi bilan keladi) ConfirmationStep ko'rsatadigan nom yangilanishini
  // ta'minlaydi — aks holda cached uz qiymat qoladi.
  useEffect(() => {
    if (providerId !== null) {
      const found = providers.find((p) => p.id === providerId);
      if (found && found.displayName !== providerDisplayName) {
        setProviderDisplayName(found.displayName);
      }
    }
  }, [providerId, providerDisplayName, providers, setProviderDisplayName]);

  const handleSelect = useCallback(
    (provider: ClientProvider) => {
      setProvider(provider.id, provider.displayName);
    },
    [setProvider],
  );

  useMainButton(t('bookingFlow.nextBtn'), onNext, providerId !== null);

  const activeProviders = providers.filter((p) => p.active);

  return (
    <div style={{ padding: '0 16px 16px' }}>
      <Typography.Text style={{ display: 'block', fontSize: 17, fontWeight: 600, color: 'var(--bl-text)', letterSpacing: -0.3, padding: '14px 0 10px' }}>
        {t('bookingFlow.provider.title')}
      </Typography.Text>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {activeProviders.map((provider) => {
          const isSelected = providerId === provider.id;
          return (
            <button
              key={provider.id}
              type="button"
              onClick={() => handleSelect(provider)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 14px',
                borderRadius: 'var(--bl-radius-lg)',
                background: isSelected ? 'rgba(51,144,236,0.10)' : 'var(--bl-surface)',
                border: isSelected
                  ? '2px solid var(--bl-primary)'
                  : '1px solid var(--bl-divider)',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'inherit',
                WebkitTapHighlightColor: 'transparent',
                width: '100%',
              }}
            >
              {/* Initials circle */}
              <div style={{
                width: 44, height: 44, borderRadius: 'var(--bl-radius)', flexShrink: 0,
                background: isSelected ? 'var(--bl-primary)' : 'var(--bl-surface)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 17, fontWeight: 700,
                color: isSelected ? '#fff' : 'var(--bl-text-secondary)',
              }}>
                {provider.displayName[0]?.toUpperCase()}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Typography.Text style={{
                  display: 'block', fontSize: 15, fontWeight: 700,
                  color: isSelected ? 'var(--bl-primary)' : 'var(--bl-text)',
                }}>
                  {provider.displayName}
                </Typography.Text>
                {provider.bio && (
                  <Typography.Text style={{ fontSize: 13, color: 'var(--bl-text-secondary)' }}>
                    {provider.bio}
                  </Typography.Text>
                )}
              </div>

              {/* Check */}
              {isSelected && (
                <div style={{
                  width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                  background: 'var(--bl-primary)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, color: '#fff',
                }}>
                  <CheckOutlined />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
